import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, MessageCircle, Send as SendIcon, Mail, Phone, ShieldCheck, CheckCircle2, Calendar, Wallet, Receipt } from "lucide-react";
import { Field, Input, Select, GoldButton, GhostButton, PageHeader } from "@/components/Ui";

function Section({ title, icon: Icon, enabled, onToggle, hint, status, children }) {
    return (
        <div className="bg-muted border border-border">
            <div className="px-5 sm:px-6 py-5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Icon className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5}/>
                    <div className="min-w-0">
                        <div className="font-serif text-lg">{title}</div>
                        {hint && <div className="text-xs text-muted-foreground truncate">{hint}</div>}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {status && <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>{status}</span>}
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!enabled} onChange={e => onToggle(e.target.checked)} data-testid={`toggle-${title.toLowerCase().replace(/\s+/g, "-")}`} className="sr-only peer"/>
                        <div className="w-10 h-5 bg-background border border-border peer-checked:bg-primary peer-checked:border-primary relative transition-colors">
                            <div className={`absolute top-0.5 ${enabled ? "left-5 bg-background" : "left-0.5 bg-muted-foreground"} w-3.5 h-3.5 transition-all`}></div>
                        </div>
                        <span className="text-xs text-muted-foreground">{enabled ? "On" : "Off"}</span>
                    </label>
                </div>
            </div>
            <div className="p-5 sm:p-6 space-y-4">{children}</div>
        </div>
    );
}

function SecretField({ label, value, masked, onChange, hint, testId }) {
    return (
        <Field label={label} hint={hint || (masked ? `Currently set: ${masked} (leave blank to keep)` : null)}>
            <Input value={value} onChange={onChange} type="password" autoComplete="off" data-testid={testId}
                placeholder={masked ? "•••• change" : "Paste value"}/>
        </Field>
    );
}

const EMPTY = {
    razorpay_enabled: false, razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "",
    whatsapp_enabled: false, whatsapp_provider: "meta", whatsapp_access_token: "", whatsapp_phone_number_id: "", whatsapp_business_account_id: "",
    telegram_enabled: false, telegram_bot_token: "",
    email_enabled: false, email_provider: "smtp", smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "",
    smtp_from_address: "", sendgrid_api_key: "", sendgrid_from_address: "",
    sms_enabled: false, twilio_account_sid: "", twilio_auth_token: "", twilio_sms_from: "",
    platform_google_enabled: false, platform_google_client_id: "", platform_google_client_secret: "",
    platform_outlook_enabled: false, platform_outlook_client_id: "", platform_outlook_client_secret: "", platform_outlook_tenant_id: "common",
    wallet_min_topup: 100, wallet_max_topup: 0, wallet_topup_presets: [500, 1000, 2500, 5000],
};

export default function AdminIntegrations() {
    const [form, setForm] = useState(EMPTY);
    const [masked, setMasked] = useState({});
    const [redirects, setRedirects] = useState({ google: "", outlook: "" });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/platform/integrations");
            const m = {};
            Object.entries(data).forEach(([k, v]) => { if (k.endsWith("_masked")) m[k.replace("_masked", "")] = v; });
            setMasked(m);
            setRedirects({
                google: data.platform_google_redirect_uri || "",
                outlook: data.platform_outlook_redirect_uri || "",
            });
            setForm(f => ({ ...f, ...Object.fromEntries(Object.entries(data).filter(([k]) => !k.endsWith("_masked") && !k.endsWith("_redirect_uri"))) }));
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const upd = (k) => (v) => setForm({ ...form, [k]: typeof v === "object" && v.target ? v.target.value : v });

    const save = async () => {
        setSaving(true);
        try {
            // Strip empty sensitive fields → backend keeps existing
            const payload = { ...form };
            await api.patch("/platform/integrations", payload);
            toast.success("Integrations saved");
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const test = async (provider) => {
        setTesting(provider);
        try {
            // Auto-save any in-form edits first so the test uses the latest values,
            // not the previously saved ones. Backend strips empty sensitive fields,
            // so this is safe even if the user didn't touch the secret.
            await api.patch("/platform/integrations", form);
            const { data } = await api.post(`/platform/integrations/test/${provider}`);
            toast.success(`${provider}: ${data.message}`);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setTesting(null); }
    };

    return (
        <>
            <PageHeader
                title="Integrations"
                subtitle="Plug in real provider keys for Razorpay, WhatsApp, Telegram, Email and SMS. Tenants inherit these globally."
                action={<GoldButton onClick={save} disabled={saving} data-testid="integrations-save-btn">{saving ? "Saving…" : "Save changes"}</GoldButton>}
            />

            <div className="bg-muted border border-primary/30 p-4 mb-8 text-xs text-muted-foreground flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" strokeWidth={1.5}/>
                <div>
                    <div className="text-foreground text-sm mb-1">Credentials are write-only.</div>
                    Once saved, values are masked and never returned. Leave secret fields blank to keep the current value; type a new value only when rotating.
                </div>
            </div>

            <div className="space-y-6">
                {/* External Calendar OAuth (platform-managed app credentials) */}
                <Section title="External Calendar OAuth" icon={Calendar} enabled={form.platform_google_enabled || form.platform_outlook_enabled}
                    onToggle={(v) => upd("platform_google_enabled")(v)}
                    hint="Platform-level OAuth credentials so firms don't need their own GCP / Azure developer apps. Each user connects their own calendar from Settings.">
                    <div className="bg-background border border-border p-3 text-[11px] text-muted-foreground leading-relaxed mb-2">
                        <strong className="text-foreground">Two-tier security model:</strong> these credentials authenticate the Law OS platform with Google / Microsoft.
                        Each law-firm user then completes their own OAuth handshake from Settings → Calendar Sync, producing per-user
                        access + refresh tokens that are encrypted at rest and scoped only to their calendar.
                    </div>

                    {/* Google sub-card */}
                    <div className="border border-border p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-sm">Google Calendar</div>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                <input type="checkbox" checked={form.platform_google_enabled}
                                    onChange={e => upd("platform_google_enabled")(e.target.checked)}
                                    className="w-4 h-4 accent-primary" data-testid="platform-google-enabled"/>
                                <span>{form.platform_google_enabled ? "Enabled" : "Disabled"}</span>
                            </label>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Register this redirect URI in <a className="text-primary hover:underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> → OAuth 2.0 Client → Authorized redirect URIs:
                        </p>
                        <div className="text-[10px] font-mono text-primary bg-background border border-border px-2 py-1 break-all" data-testid="platform-google-redirect-uri">
                            {redirects.google || "—"}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Client ID" hint="Public, ends with .apps.googleusercontent.com">
                                <Input value={form.platform_google_client_id} onChange={upd("platform_google_client_id")} placeholder="xxxxx.apps.googleusercontent.com" data-testid="platform-google-client-id"/>
                            </Field>
                            <SecretField label="Client secret" value={form.platform_google_client_secret} masked={masked.platform_google_client_secret} onChange={upd("platform_google_client_secret")} testId="platform-google-client-secret"/>
                        </div>
                        <div className="flex justify-end pt-1">
                            <GhostButton onClick={() => test("google_calendar")} disabled={testing === "google_calendar"} data-testid="platform-google-test-btn">
                                {testing === "google_calendar" ? "Testing…" : "Test connection"}
                            </GhostButton>
                        </div>
                    </div>

                    {/* Microsoft sub-card */}
                    <div className="border border-border p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-sm">Microsoft Outlook (Graph API)</div>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                <input type="checkbox" checked={form.platform_outlook_enabled}
                                    onChange={e => upd("platform_outlook_enabled")(e.target.checked)}
                                    className="w-4 h-4 accent-primary" data-testid="platform-outlook-enabled"/>
                                <span>{form.platform_outlook_enabled ? "Enabled" : "Disabled"}</span>
                            </label>
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Register this redirect URI in <a className="text-primary hover:underline" href="https://portal.azure.com" target="_blank" rel="noopener noreferrer">Azure Portal</a> → App registration → Authentication → Web platform:
                        </p>
                        <div className="text-[10px] font-mono text-primary bg-background border border-border px-2 py-1 break-all" data-testid="platform-outlook-redirect-uri">
                            {redirects.outlook || "—"}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Application (client) ID">
                                <Input value={form.platform_outlook_client_id} onChange={upd("platform_outlook_client_id")} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="platform-outlook-client-id"/>
                            </Field>
                            <SecretField label="Client secret value" value={form.platform_outlook_client_secret} masked={masked.platform_outlook_client_secret} onChange={upd("platform_outlook_client_secret")} testId="platform-outlook-client-secret"/>
                            <Field label="Directory (tenant) ID" hint="Use 'common' for multi-tenant apps.">
                                <Input value={form.platform_outlook_tenant_id} onChange={upd("platform_outlook_tenant_id")} placeholder="common" data-testid="platform-outlook-tenant-id"/>
                            </Field>
                        </div>
                        <div className="flex justify-end pt-1">
                            <GhostButton onClick={() => test("outlook_calendar")} disabled={testing === "outlook_calendar"} data-testid="platform-outlook-test-btn">
                                {testing === "outlook_calendar" ? "Testing…" : "Test connection"}
                            </GhostButton>
                        </div>
                    </div>
                </Section>

                {/* Razorpay */}
                <Section title="Razorpay" icon={CreditCard} enabled={form.razorpay_enabled} onToggle={v => upd("razorpay_enabled")(v)} hint="Subscriptions, AutoPay, refunds, payment links">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Key ID" hint="Public, starts with rzp_test_… or rzp_live_…">
                            <Input value={form.razorpay_key_id} onChange={upd("razorpay_key_id")} placeholder="rzp_test_xxxxxxxxxxxxx" data-testid="razorpay-key-id-input"/>
                        </Field>
                        <SecretField label="Key Secret" value={form.razorpay_key_secret} masked={masked.razorpay_key_secret} onChange={upd("razorpay_key_secret")} testId="razorpay-key-secret-input"/>
                        <SecretField label="Webhook secret" value={form.razorpay_webhook_secret} masked={masked.razorpay_webhook_secret} onChange={upd("razorpay_webhook_secret")} testId="razorpay-webhook-secret-input"/>
                    </div>
                    <div className="flex justify-end pt-2"><GhostButton onClick={() => test("razorpay")} disabled={testing === "razorpay"} data-testid="razorpay-test-btn">{testing === "razorpay" ? "Testing…" : "Test connection"}</GhostButton></div>
                </Section>

                {/* WhatsApp */}
                <Section title="WhatsApp" icon={MessageCircle} enabled={form.whatsapp_enabled} onToggle={v => upd("whatsapp_enabled")(v)} hint="Hearing reminders, invoices, broadcasts via Meta Cloud API">
                    <Field label="Provider"><Select value={form.whatsapp_provider} onChange={upd("whatsapp_provider")} options={[{value: "meta", label: "Meta Cloud API"}]} data-testid="whatsapp-provider-select"/></Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <SecretField label="Access token" value={form.whatsapp_access_token} masked={masked.whatsapp_access_token} onChange={upd("whatsapp_access_token")} hint="From Meta Developers → System User" testId="whatsapp-token-input"/>
                        <SecretField label="Phone number ID" value={form.whatsapp_phone_number_id} masked={masked.whatsapp_phone_number_id} onChange={upd("whatsapp_phone_number_id")} testId="whatsapp-phone-id-input"/>
                        <Field label="Business account ID (optional)">
                            <Input value={form.whatsapp_business_account_id} onChange={upd("whatsapp_business_account_id")} data-testid="whatsapp-business-id-input"/>
                        </Field>
                    </div>
                    <div className="flex justify-end pt-2"><GhostButton onClick={() => test("whatsapp")} disabled={testing === "whatsapp"} data-testid="whatsapp-test-btn">{testing === "whatsapp" ? "Testing…" : "Test connection"}</GhostButton></div>
                </Section>

                {/* Telegram */}
                <Section title="Telegram" icon={SendIcon} enabled={form.telegram_enabled} onToggle={v => upd("telegram_enabled")(v)} hint="Internal bot notifications">
                    <SecretField label="Bot token" value={form.telegram_bot_token} masked={masked.telegram_bot_token} onChange={upd("telegram_bot_token")} hint="From @BotFather" testId="telegram-token-input"/>
                    <div className="flex justify-end"><GhostButton onClick={() => test("telegram")} disabled={testing === "telegram"} data-testid="telegram-test-btn">{testing === "telegram" ? "Testing…" : "Test connection"}</GhostButton></div>
                </Section>

                {/* Email */}
                <Section title="Email" icon={Mail} enabled={form.email_enabled} onToggle={v => upd("email_enabled")(v)} hint="Transactional emails &mdash; SMTP or SendGrid">
                    <Field label="Provider"><Select value={form.email_provider} onChange={upd("email_provider")} options={[{value: "smtp", label: "SMTP"}, {value: "sendgrid", label: "SendGrid"}]} data-testid="email-provider-select"/></Field>
                    {form.email_provider === "smtp" ? (
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Host"><Input value={form.smtp_host} onChange={upd("smtp_host")} placeholder="smtp.gmail.com" data-testid="smtp-host-input"/></Field>
                            <Field label="Port"><Input type="number" value={form.smtp_port} onChange={upd("smtp_port")} data-testid="smtp-port-input"/></Field>
                            <Field label="Username"><Input value={form.smtp_username} onChange={upd("smtp_username")} data-testid="smtp-username-input"/></Field>
                            <SecretField label="Password" value={form.smtp_password} masked={masked.smtp_password} onChange={upd("smtp_password")} testId="smtp-password-input"/>
                            <Field label="From address"><Input value={form.smtp_from_address} onChange={upd("smtp_from_address")} placeholder="hello@firm.com" data-testid="smtp-from-input"/></Field>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-4">
                            <SecretField label="SendGrid API key" value={form.sendgrid_api_key} masked={masked.sendgrid_api_key} onChange={upd("sendgrid_api_key")} testId="sendgrid-key-input"/>
                            <Field label="From address"><Input value={form.sendgrid_from_address} onChange={upd("sendgrid_from_address")} data-testid="sendgrid-from-input"/></Field>
                        </div>
                    )}
                    <div className="flex justify-end pt-2">
                        <GhostButton onClick={async () => {
                            const to = window.prompt("Send test email to:", "");
                            if (!to) return;
                            try {
                                const { data } = await api.post("/admin/email/test", { to });
                                toast.success(`Sent via ${data?.mode || "provider"} → ${to}`);
                            } catch (e) { toast.error(apiError(e)); }
                        }} data-testid="email-test-btn">Send test email</GhostButton>
                    </div>
                </Section>

                {/* SMS via Twilio */}
                <Section title="SMS (Twilio)" icon={Phone} enabled={form.sms_enabled} onToggle={v => upd("sms_enabled")(v)} hint="OTP fallback, reminders">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Account SID"><Input value={form.twilio_account_sid} onChange={upd("twilio_account_sid")} data-testid="twilio-sid-input"/></Field>
                        <SecretField label="Auth token" value={form.twilio_auth_token} masked={masked.twilio_auth_token} onChange={upd("twilio_auth_token")} testId="twilio-token-input"/>
                        <Field label="From number"><Input value={form.twilio_sms_from} onChange={upd("twilio_sms_from")} placeholder="+12025550123" data-testid="twilio-from-input"/></Field>
                    </div>
                </Section>

                {/* Wallet recharge configuration — controls the firm wallet UI for all tenants */}
                <div className="bg-muted border border-border" data-testid="wallet-config-section">
                    <div className="px-5 sm:px-6 py-5 border-b border-border flex items-center gap-3">
                        <Wallet className="w-5 h-5 text-primary shrink-0" strokeWidth={1.5}/>
                        <div>
                            <div className="font-serif text-lg">Wallet recharge</div>
                            <div className="text-xs text-muted-foreground">Controls the firm-wallet quick-pick chips, minimum top-up, and ceiling for every tenant.</div>
                        </div>
                    </div>
                    <div className="p-5 sm:p-6 space-y-4">
                        <div className="grid sm:grid-cols-3 gap-4">
                            <Field label="Minimum top-up (₹)" hint="Razorpay's lowest accepted amount is ₹1. Most firms keep this at ₹100 to avoid trivial transactions.">
                                <Input type="number" min="1" value={form.wallet_min_topup} onChange={upd("wallet_min_topup")} data-testid="wallet-min-topup-input"/>
                            </Field>
                            <Field label="Maximum top-up (₹)" hint="0 = no cap. Set a ceiling if you want to force enterprise plans for larger top-ups.">
                                <Input type="number" min="0" value={form.wallet_max_topup} onChange={upd("wallet_max_topup")} data-testid="wallet-max-topup-input"/>
                            </Field>
                            <Field label="Quick-pick chips (comma-separated ₹)" hint="Shown as one-click buttons. e.g. 500, 1000, 2500, 5000">
                                <Input value={Array.isArray(form.wallet_topup_presets) ? form.wallet_topup_presets.join(", ") : ""}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        const parsed = v.split(",").map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0);
                                        setForm(f => ({ ...f, wallet_topup_presets: parsed }));
                                    }}
                                    placeholder="500, 1000, 2500, 5000"
                                    data-testid="wallet-presets-input"/>
                            </Field>
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                            Live preview: <span className="text-muted-foreground">{(form.wallet_topup_presets || []).map(p => `₹${p}`).join(" · ") || "no chips"}</span>
                            {form.wallet_max_topup > 0 && <span className="ml-3">Max: <span className="text-muted-foreground">₹{form.wallet_max_topup}</span></span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* v2.17.9 · Meta WhatsApp template submission + approval status */}
            <MetaTemplatesCard/>

            {/* Deropo WhatsApp — primary provider for cause-list & invoice dispatch */}
            <DeropoCard/>

            {/* v2.18 · Auto invoice via WhatsApp on payment_captured */}
            <InvoiceWhatsAppCard/>

            <div className="mt-10 flex justify-end">
                <GoldButton onClick={save} disabled={saving} data-testid="integrations-save-btn-bottom">{saving ? "Saving…" : "Save all integrations"}</GoldButton>
            </div>
        </>
    );
}


// Controls which channel (Meta) the `send_invoice_whatsapp`
// automation action uses when no explicit provider is set on the rule.
function DeropoCard() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ access_token: "", instance_id: "", webhook_url: "", enabled: true });
    const [saving, setSaving] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [qrBusy, setQrBusy] = useState(false);
    const [qrResp, setQrResp] = useState(null);
    const [syncBusy, setSyncBusy] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [webhookEvents, setWebhookEvents] = useState(null);
    const [eventsBusy, setEventsBusy] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [replyingFor, setReplyingFor] = useState(null); // phone being replied to
    const [replyResults, setReplyResults] = useState({});  // {phone: resultObj}
    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/deropo/config");
            setCfg(r.data);
            setForm(f => ({ ...f, instance_id: r.data.instance_id || "",
                                    webhook_url: r.data.webhook_url || "",
                                    enabled: r.data.enabled }));
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.access_token && !cfg?.configured) {
            toast.error("Access token required for first-time setup"); return;
        }
        setSaving(true);
        try {
            // Backend accepts blank access_token only via a body with the field
            // present-and-set; if user left it blank on save (rotate later),
            // preserve the existing token by sending a benign placeholder.
            const body = { ...form,
                access_token: form.access_token || "__KEEP__",
            };
            if (body.access_token === "__KEEP__") delete body.access_token;
            await api.post("/admin/deropo/config", body);
            toast.success("Deropo config saved");
            setForm(f => ({ ...f, access_token: "" }));
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const testSend = async () => {
        if (!testTo.trim()) { toast.error("Enter a phone number (E.164, e.g. 919812345678)"); return; }
        setTestBusy(true); setTestResult(null);
        try {
            const { data } = await api.post("/admin/deropo/test",
                { to_number: testTo.trim(), message: "LawOS · Deropo smoke test" });
            setTestResult(data);
            if (data.ok) toast.success("Deropo API accepted · check phone + see response below");
            else toast.error("Send failed: " + (data.reason || data.response));
        } catch (e) { toast.error(apiError(e)); }
        finally { setTestBusy(false); }
    };

    const fetchQr = async () => {
        setQrBusy(true); setQrResp(null);
        try {
            const { data } = await api.post("/admin/deropo/qrcode");
            setQrResp(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setQrBusy(false); }
    };

    const syncWebhook = async () => {
        if (!form.webhook_url.trim() && !cfg?.webhook_url) {
            toast.error("Enter the Webhook URL first and save the config"); return;
        }
        setSyncBusy(true); setSyncResult(null);
        try {
            // Auto-save form first so the new instance_id is in DB before sync
            if (form.instance_id && form.instance_id !== cfg?.instance_id) {
                const body = { ...form, access_token: form.access_token || "__KEEP__" };
                if (body.access_token === "__KEEP__") delete body.access_token;
                await api.post("/admin/deropo/config", body);
                await load();
            }
            const { data } = await api.post("/admin/deropo/sync-webhook");
            setSyncResult(data);
            if (data.ok) {
                toast.success(`Webhook synced! Instance: ${data.instance_id_used || "saved"}`);
            } else {
                toast.error(`Deropo returned HTTP ${data.status_code}: ${data.response?.slice(0, 120)}`);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setSyncBusy(false); }
    };

    const fetchWebhookEvents = async () => {
        setEventsBusy(true);
        try {
            const { data } = await api.get("/admin/deropo/webhook-events?limit=10");
            setWebhookEvents(data.events || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setEventsBusy(false); }
    };

    const replyWithAI = async (phone, message) => {
        const key = `${phone}_${message?.slice(0,20)}`;
        setReplyingFor(key);
        try {
            const { data } = await api.post("/admin/wa-ai-config/simulate", { phone, message });
            setReplyResults(r => ({ ...r, [key]: data }));
            if (data.ok) toast.success(`AI reply sent to ${phone.slice(0,6)}***`);
            else toast.error("AI pipeline failed: " + data.error);
        } catch (e) { toast.error(apiError(e)); }
        finally { setReplyingFor(null); }
    };

    return (
        <div className="mt-6 border border-primary/30 rounded-2xl bg-card" data-testid="deropo-card">
            <div className="p-5 border-b border-border flex items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-primary"/> Deropo WhatsApp <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border border-border px-1.5 py-0.5 rounded">unofficial</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Cloud-hosted WhatsApp API (cloud.deropo.co.in). <span className="text-primary">Coexists</span> with your Meta WABA above — no changes to that config.
                    </div>
                </div>
                {cfg && (
                    <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Status</div>
                        <div className={`text-xs ${cfg.configured ? "text-emerald-500" : "text-amber-500"}`}
                            data-testid="deropo-status">
                            {cfg.configured ? (cfg.enabled ? "Configured · Enabled" : "Configured · Disabled") : "Not configured"}
                        </div>
                    </div>
                )}
            </div>
            <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Access token" hint={cfg?.configured ? `Current: ${cfg.access_token_masked} · leave blank to keep` : "Get this from Deropo dashboard"}>
                        <Input type="password"
                            value={form.access_token}
                            onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))}
                            placeholder={cfg?.configured ? cfg.access_token_masked : "Paste access_token"}
                            data-testid="deropo-token-input"/>
                    </Field>
                    <Field label="Instance ID" hint="Deropo instance identifier">
                        <Input value={form.instance_id}
                            onChange={e => setForm(f => ({ ...f, instance_id: e.target.value }))}
                            placeholder="e.g. 668A9F4E42AB1"
                            data-testid="deropo-instance-input"/>
                    </Field>
                    <Field label="Webhook URL" hint="Your LawOS server URL — Deropo POSTs incoming messages here. Save config, then click Sync.">
                        <Input value={form.webhook_url}
                            onChange={e => setForm(f => ({ ...f, webhook_url: e.target.value }))}
                            placeholder="https://yourdomain.com/api/webhooks/deropo"
                            data-testid="deropo-webhook-input"/>
                    </Field>
                    <div className="flex items-end">
                        <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                            <input type="checkbox" checked={form.enabled}
                                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                                data-testid="deropo-enabled-toggle"/>
                            Enable Deropo provider
                        </label>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                    <GoldButton onClick={save} disabled={saving} data-testid="deropo-save">
                        {saving ? "Saving…" : "Save Deropo config"}
                    </GoldButton>
                    {cfg?.configured && (
                        <>
                            {(cfg?.webhook_url || form.webhook_url) && (
                                <GhostButton onClick={syncWebhook} disabled={syncBusy} data-testid="deropo-sync-webhook-btn"
                                    title={`Push ${form.webhook_url || cfg.webhook_url} to Deropo's set_webhook API`}>
                                    {syncBusy ? "Syncing…" : "Sync Webhook to Deropo"}
                                </GhostButton>
                            )}
                            <div className="flex items-center gap-2">
                                <Input value={testTo} onChange={e => setTestTo(e.target.value)}
                                    placeholder="9946006050 or +91 99460 06050"
                                    title="India numbers auto-prefix +91. Country code required for other regions."
                                    data-testid="deropo-test-to"/>
                                <GhostButton onClick={testSend} disabled={testBusy} data-testid="deropo-test-btn">
                                    {testBusy ? "Sending…" : "Test send"}
                                </GhostButton>
                            </div>
                            <GhostButton onClick={fetchQr} disabled={qrBusy} data-testid="deropo-qr-btn">
                                {qrBusy ? "Fetching…" : "Fetch QR"}
                            </GhostButton>
                            <GhostButton onClick={fetchWebhookEvents} disabled={eventsBusy} data-testid="deropo-events-btn">
                                {eventsBusy ? "Loading…" : "Check Incoming Events"}
                            </GhostButton>
                        </>
                    )}
                </div>
                {/* Sync result panel */}
                {syncResult && (
                    <div className={`border rounded p-2 text-[10px] font-mono ${syncResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}
                        data-testid="deropo-sync-result">
                        <div className={`font-bold mb-1 ${syncResult.ok ? "text-emerald-500" : "text-red-500"}`}>
                            {syncResult.message || (syncResult.ok ? "Sync SUCCESS" : "Sync FAILED")}
                        </div>
                        <div className="text-muted-foreground">Instance: {syncResult.instance_id_used} · HTTP {syncResult.status_code}</div>
                        {syncResult.is_html_response && (
                            <div className="text-amber-400 mt-1">
                                Deropo returned HTML (not JSON). This usually means the access_token or instance_id is wrong for this instance.
                                Check your Deropo dashboard and make sure you are using the correct access token for instance {syncResult.instance_id_used}.
                            </div>
                        )}
                        {!syncResult.is_html_response && (
                            <div className="text-muted-foreground mt-1 whitespace-pre-wrap">Response: {syncResult.response?.slice(0, 200)}</div>
                        )}
                    </div>
                )}
                {/* Test send result — always show raw Deropo response since they return 200 even on failure */}
                {testResult && (
                    <div className={`border rounded p-2 text-[10px] font-mono ${testResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}
                        data-testid="deropo-test-result">
                        <div className={`font-bold mb-1 ${testResult.ok ? "text-emerald-500" : "text-red-500"}`}>
                            {testResult.ok
                                ? "Deropo API ACCEPTED (HTTP 200) — check if the message arrived on the phone below:"
                                : "Deropo API REJECTED — check response below:"}
                        </div>
                        <div className="text-muted-foreground">To: {testResult.normalized_to || testResult.to_number}</div>
                        <div className="text-muted-foreground whitespace-pre-wrap mt-1">
                            Raw Deropo response: {testResult.response || testResult.reason || "(empty)"}
                        </div>
                        {testResult.ok && (
                            <div className="text-amber-400 mt-2">
                                If the message did NOT arrive → the Deropo instance may not be connected to WhatsApp. Click "Fetch QR" and scan with the WhatsApp app that should be the bot number.
                            </div>
                        )}
                    </div>
                )}
                {qrResp && (
                    <div className="border border-border rounded-lg p-3 bg-background text-xs" data-testid="deropo-qr-response">
                        {qrResp.qr_image_data_uri ? (
                            <>
                                <div className="mb-2 text-primary">Scan this QR with your phone's WhatsApp → Linked Devices</div>
                                <img src={qrResp.qr_image_data_uri} alt="Deropo QR"
                                    className="mx-auto max-w-xs border border-border rounded"/>
                            </>
                        ) : qrResp.qr_page_url ? (
                            <div>
                                Deropo returned an HTML page. Open the QR here:{" "}
                                <a href={qrResp.qr_page_url} target="_blank" rel="noreferrer"
                                    className="text-primary hover:underline break-all">{qrResp.qr_page_url}</a>
                            </div>
                        ) : (
                            <div className="text-muted-foreground">
                                <div className="mb-2">{qrResp.hint || "Deropo returned a non-standard response."}</div>
                                <div className="font-mono text-[10px] whitespace-pre-wrap opacity-70 max-h-32 overflow-auto">
                                    {typeof qrResp === "string" ? qrResp : JSON.stringify(qrResp, null, 2).slice(0, 800)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {webhookEvents !== null && (
                    <div className="border border-border rounded-lg p-3 bg-background" data-testid="deropo-webhook-events">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-medium text-foreground">
                                Recent Incoming Events ({webhookEvents.length})
                            </div>
                            <button onClick={() => setWebhookEvents(null)}
                                className="text-[10px] text-muted-foreground hover:text-foreground">✕ close</button>
                        </div>
                        {webhookEvents.length === 0 ? (
                            <div className="text-xs text-amber-500 font-medium">
                                No events received yet — Deropo has not POSTed to your webhook URL since this server started.
                                <br/>Check: (a) is the webhook URL correct and reachable? (b) did you send a message after syncing?
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {webhookEvents.map((ev, i) => {
                                    const p = ev.payload || {};
                                    const isInbound = (p.type === "message" && p.fromMe === false)
                                        || (p.event === "message")
                                        || (Array.isArray(p.messages) && p.messages?.some(m => !m?.key?.fromMe));
                                    const senderPhone = p.from || p.sender || (Array.isArray(p.messages) ? p.messages[0]?.key?.remoteJid?.replace("@s.whatsapp.net","") : "");
                                    const msgBody = p.body || p.text || (Array.isArray(p.messages) ? p.messages[0]?.message?.conversation : "");
                                    const replyKey = `${senderPhone}_${(msgBody||"").slice(0,20)}`;
                                    const replyResult = replyResults[replyKey];
                                    return (
                                        <div key={i} className={`text-[10px] border rounded p-2 font-mono whitespace-pre-wrap break-all ${isInbound ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}>
                                            <div className="flex items-center gap-2 mb-1 text-[9px]">
                                                <span className="text-muted-foreground">{ev.received_at?.slice(0, 19).replace("T"," ")} UTC</span>
                                                {isInbound && <span className="text-emerald-500 font-bold">INBOUND MSG</span>}
                                                {isInbound && senderPhone && msgBody && (
                                                    <button
                                                        onClick={() => replyWithAI(senderPhone, msgBody)}
                                                        disabled={replyingFor === replyKey}
                                                        className="ml-auto px-2 py-0.5 rounded text-[9px] font-semibold bg-primary/20 text-primary hover:bg-primary/30 disabled:opacity-50 transition-colors"
                                                        data-testid={`deropo-reply-ai-${i}`}>
                                                        {replyingFor === replyKey ? "Replying…" : "Reply with AI"}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="max-h-24 overflow-auto">{JSON.stringify(p, null, 2).slice(0, 600)}</div>
                                            {replyResult && (
                                                <div className={`mt-1 p-1 rounded border text-[9px] ${replyResult.ok ? "border-emerald-500/40 text-emerald-500" : "border-red-500/40 text-red-400"}`}>
                                                    {replyResult.ok ? `AI replied: "${(replyResult.reply_sent||"").slice(0,80)}…"` : `Failed: ${replyResult.error}`}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
// Controls which channel (Deropo/Meta) the `send_invoice_whatsapp`
// automation action uses when no explicit provider is set on the rule.
// Default: Deropo (no template approval required).
function InvoiceWhatsAppCard() {
    const [cfg, setCfg] = useState({ enabled: true, provider: "deropo" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/admin/invoice-whatsapp");
                setCfg({ enabled: !!r.data.enabled, provider: r.data.provider || "deropo" });
            } catch { /* first-time — keep defaults */ }
        })();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/invoice-whatsapp", cfg);
            toast.success("Invoice WhatsApp config saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="mt-6 border border-primary/30 rounded-2xl bg-card" data-testid="invoice-whatsapp-card">
            <div className="p-5 border-b border-border">
                <div className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-primary"/> Invoice → WhatsApp
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                    When a tenant's Razorpay payment is captured, Law OS auto-generates a GST invoice and can push the PDF over WhatsApp. Pick the default provider used by the <code className="text-primary">send_invoice_whatsapp</code> automation action.
                </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Enabled">
                    <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
                        <input type="checkbox" checked={cfg.enabled}
                            onChange={e => setCfg(c => ({ ...c, enabled: e.target.checked }))}
                            data-testid="invoice-whatsapp-enabled"/>
                        Send invoice via WhatsApp on payment
                    </label>
                </Field>
                <Field label="Default provider" hint="Deropo needs no template approval">
                    <select value={cfg.provider}
                        onChange={e => setCfg(c => ({ ...c, provider: e.target.value }))}
                        data-testid="invoice-whatsapp-provider"
                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground">
                        <option value="deropo">Deropo (recommended — no template approval)</option>
                        <option value="meta">Meta WABA (official — requires approved template)</option>
                        <option value="deropo_then_meta">Deropo first, fallback Meta</option>
                    </select>
                </Field>
                <div className="flex items-end">
                    <GoldButton onClick={save} disabled={saving} data-testid="invoice-whatsapp-save">
                        {saving ? "Saving…" : "Save"}
                    </GoldButton>
                </div>
            </div>
        </div>
    );
}




// ─────────── Meta WhatsApp Template Submission ───────────
// Lets super-admin submit templates for Meta approval directly from LawOS,
// no need to log into Meta Business Manager. Also lists current templates
// with their approval status (PENDING / APPROVED / REJECTED).
function MetaTemplatesCard() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        name: "", language: "en", category: "UTILITY", body_text: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/whatsapp/templates");
            setTemplates(r.data.templates || []);
        } catch (e) {
            const msg = apiError(e);
            if (!/not configured/i.test(msg)) toast.error(msg);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        if (!form.name.trim() || !form.body_text.trim()) {
            toast.error("Name and body text required"); return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/whatsapp/templates", {
                name: form.name,
                language: form.language,
                category: form.category,
                components: [{ type: "BODY", text: form.body_text }],
            });
            toast.success("Template submitted to Meta · approval 24-48 hrs");
            setForm({ name: "", language: "en", category: "UTILITY", body_text: "" });
            setCreating(false);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSubmitting(false); }
    };

    const del = async (name) => {
        if (!window.confirm(`Delete template "${name}" from Meta?`)) return;
        try {
            await api.delete(`/admin/whatsapp/templates/${name}`);
            toast.success("Template deleted");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const badge = (s) => {
        const map = { APPROVED: "emerald-500", PENDING: "amber-500",
                        REJECTED: "rose-500", PAUSED: "sky-500" };
        const color = map[s] || "muted-foreground";
        return <span className={`text-[10px] uppercase tracking-[0.14em] text-${color} border border-${color}/40 px-1.5 py-0.5 rounded`}>{s}</span>;
    };

    return (
        <div className="mt-6 border border-primary/30 rounded-2xl bg-card" data-testid="meta-templates-card">
            <div className="p-5 border-b border-border flex items-center justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-primary"/> Meta WhatsApp templates
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Submit new templates for Meta approval + see current approval status. Requires Meta WABA config above.
                    </div>
                </div>
                <button onClick={() => setCreating(v => !v)}
                    data-testid="meta-template-new-btn"
                    className="text-xs uppercase tracking-[0.14em] px-3 py-2 border border-primary/40 text-primary hover:bg-primary/10 rounded-lg">
                    {creating ? "Cancel" : "+ Submit template"}
                </button>
            </div>
            {creating && (
                <div className="p-5 border-b border-border grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="meta-template-form">
                    <Field label="Template name" hint="lowercase_snake_case (e.g. invoice_paid)">
                        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="invoice_paid" data-testid="mt-name"/>
                    </Field>
                    <Field label="Language" hint="BCP-47 code">
                        <Input value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
                            placeholder="en" data-testid="mt-lang"/>
                    </Field>
                    <Field label="Category">
                        <select value={form.category}
                            onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                            data-testid="mt-category"
                            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground">
                            <option>UTILITY</option>
                            <option>MARKETING</option>
                            <option>AUTHENTICATION</option>
                        </select>
                    </Field>
                    <div className="md:col-span-3">
                        <Field label="Body text" hint="Use {{1}}, {{2}}… as variable placeholders per Meta rules">
                            <textarea value={form.body_text}
                                onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
                                rows={3}
                                data-testid="mt-body"
                                placeholder="Hi {{1}}, your invoice #{{2}} for ₹{{3}} is now paid. Thank you!"
                                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground"/>
                        </Field>
                    </div>
                    <div className="md:col-span-3 flex justify-end">
                        <GoldButton onClick={submit} disabled={submitting} data-testid="mt-submit">
                            {submitting ? "Submitting…" : "Submit to Meta"}
                        </GoldButton>
                    </div>
                </div>
            )}
            <div className="p-5">
                {loading ? (
                    <div className="text-xs text-muted-foreground">Loading templates…</div>
                ) : templates.length === 0 ? (
                    <div className="text-xs text-muted-foreground" data-testid="meta-templates-empty">
                        No templates yet. Submit your first one above.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {templates.map(t => (
                            <div key={t.id || t.name} className="flex items-center gap-2 border border-border rounded-lg p-3"
                                data-testid={`meta-template-row-${t.name}`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-foreground font-mono">{t.name}</span>
                                        {badge(t.status)}
                                        <span className="text-[10px] text-muted-foreground">{t.category} · {t.language}</span>
                                    </div>
                                    {t.rejected_reason && (
                                        <div className="text-[10px] text-rose-500 mt-1">Rejected: {t.rejected_reason}</div>
                                    )}
                                </div>
                                <button onClick={() => del(t.name)}
                                    data-testid={`meta-template-delete-${t.name}`}
                                    className="text-[11px] text-rose-500 hover:bg-rose-500/10 rounded p-1.5">
                                    delete
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

